import { BodyMedium, HeadingMedium, theme } from '@arnold/common';
import { Field } from 'formik';
import { Card } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { SupervisorTeamSelect, TeamWithSubTeams } from '../../../screens/Teams/SupervisorTeamSelect';
import { MemberWithLeaderOfTeams } from '../../../screens/Teams/TeamsAndPeople';
import { getDisabledSubTeams, getRootTeamsOnly, SELECT_ALL_ID } from './teamSelectionUtils';

type ReportsSectionProps = {
  firstname?: string | null;
  surname?: string | null;
  structuredTeams: TeamWithSubTeams[];
  reportTeamIds: string[];
  respondent?: MemberWithLeaderOfTeams;
  onChange: (newReportTeamIds: string[]) => void;
  onBlur: (e: React.FocusEvent<any>) => void;
};

const ReportsSection = ({
  firstname,
  surname,
  structuredTeams,
  reportTeamIds,
  respondent,
  onChange,
  onBlur,
}: ReportsSectionProps) => {
  const { t } = useTranslation(['updateRespondent', 'addRespondent']);

  const realReportTeamIds = reportTeamIds.filter((id) => id !== SELECT_ALL_ID);
  const rootReportTeamsCount = getRootTeamsOnly(realReportTeamIds, structuredTeams).length;
  const subordinateReportTeamsCount = realReportTeamIds.length - rootReportTeamsCount;

  return (
    <Card className="p-8">
      <Card.Body className="p-0" data-cy="report-section">
        <HeadingMedium className="m-0 mb-7">{t('ReportsSection')}</HeadingMedium>
        <BodyMedium className="m-0 mb-7" color={theme.colors.text.secondary}>
          {firstname} {surname} {t('ReportsSectionDescription')}{' '}
          {reportTeamIds.includes(SELECT_ALL_ID) ? (
            <span className="font-weight-bold">{t('allTeams').toLowerCase()}</span>
          ) : (
            <>
              <span className="font-weight-bold">
                {rootReportTeamsCount}{' '}
                {t('ReportsSectionDescription2', {
                  postProcess: 'interval',
                  count: rootReportTeamsCount,
                })}
              </span>
              {subordinateReportTeamsCount > 0 && (
                <>
                  {' '}
                  <Trans
                    t={t}
                    i18nKey="ReportsSectionSubordinateTeams"
                    count={subordinateReportTeamsCount}
                    values={{
                      postProcess: 'interval',
                      count: subordinateReportTeamsCount,
                    }}
                    components={[<span className="font-weight-bold" />]}
                  />
                </>
              )}
            </>
          )}
        </BodyMedium>
        <Field
          component={SupervisorTeamSelect}
          name="reportTeamIds"
          teams={[
            {
              id: SELECT_ALL_ID,
              name: t('allTeams'),
              subTeams: structuredTeams,
            },
          ]}
          value={getRootTeamsOnly(reportTeamIds, structuredTeams)}
          onChange={onChange}
          onBlur={onBlur}
          multiselect={true}
          permanentlyEnabledTeamIds={[
            ...(respondent?.leaderOfTeams.map((t) => t.id) ?? []),
            ...getDisabledSubTeams(reportTeamIds, structuredTeams),
          ]}
          filterMultiValueSelectedIndicators={(id: string) => {
            // if all teams are selected, we should not show the "all teams" option
            if (reportTeamIds.includes(SELECT_ALL_ID)) {
              return id === SELECT_ALL_ID;
            }
            return true;
          }}
        />
      </Card.Body>
    </Card>
  );
};

export default ReportsSection;
